/**
 * Clearing a plugin's device-local data when the person's access to its
 * workspace data NARROWS (ADR-034 + ADR-031). The plugin local store keeps
 * working data on this device, keyed by (app, workspace, user); data the
 * person read while they could must not outlive that access here.
 *
 * Best effort and per device by design. There is no push channel for access
 * changes, so the host checks when it mounts the app, when its access sheet
 * closes, and when a call comes back FORBIDDEN: it asks the person's own
 * access (`GET …/access/mine`, the resolver every request uses) and compares
 * it with what it last saw on this device. Narrowed means any of:
 *   - the app is no longer admitted here, or the person left — the answer's
 *     `admitted: false` (its approval revoked while roles remain: they reach
 *     nothing), or the route's own NOT_FOUND; either only after this device
 *     saw access (the same 404 means "never admitted", and an unapproved app
 *     keeps local data too); a 401, a 429, a bare 404 or a network failure
 *     means "unknown" and clears nothing, so an expired session, a throttle
 *     or a rollback never wipes a recovery journal (ADR-031);
 *   - a role they held is gone, or a permission they had is gone;
 *   - an audience they read is gone (ADR-034 phase 2: removed from a reader's
 *     roles, or deprecated by the app) — compared only when both this
 *     device's record and the server's answer list audiences, so an older
 *     record or an older server is never a narrowing by itself;
 *   - roles were turned on (legacy tier rules → roles);
 *   - their workspace tier dropped.
 *
 * The check runs inside a hold on the namespace (`holdPluginLocalStoreNamespace`):
 * the plugin's own calls for it wait until the answer is in, so it cannot
 * read the old data before a clear or write it back after one. The "last
 * seen" record moves on only once a needed clear succeeded, so a failed
 * clear is tried again next time. Concurrent checks of one namespace share
 * one request; the refusal trigger is also rate-limited.
 *
 * A clear made while the app is OPEN (the access sheet closed, a refusal, or
 * a mount whose hold lapsed) cannot stop the app writing back what it read
 * before it: the next autosave restores it, and the record has already moved
 * to the narrowed access, so no later look would clear again. Such a clear
 * leaves a "clear again" marker for the namespace, and the app's next mount
 * clears once more FIRST — inside the mount's hold, before anything is asked
 * — then drops the marker. That second clear also takes whatever the app
 * wrote legitimately after the first; it is the price of not letting data
 * read under access the person lost outlive it here.
 *
 * The namespace is always the HOST's (the arche the bridge acts as, the
 * acting workspace, the session's user), never anything a plugin said.
 *
 * Never throws, and never blocks the app for longer than the hold: a failed
 * check leaves the store as it was, and its failures go to telemetry.
 */
import { api } from '@/lib/api';
import { captureMessage } from '@/lib/telemetry';
import { holdPluginLocalStoreNamespace } from '@fias/client-entities';

const KEY_PREFIX = 'fias.app-roles.v1';
/** "Clear this namespace again at its next mount" markers (see the header). */
const RECLEAR_PREFIX = 'fias.app-roles.reclear.v1';
/**
 * How long the plugin's own calls may wait for the check before they run —
 * kept below the sign-out drain's bound (PLUGIN_LOCAL_STORE_SIGN_OUT_TIMEOUT_MS,
 * 3 s), so a check never makes a sign-out overrun.
 */
const MAX_HOLD_MS = 2_500;
/** At most one refusal-triggered check per namespace in this window. */
const REFUSAL_COOLDOWN_MS = 10_000;

export interface RoleLossNamespace {
  /** The app whose access is asked about. */
  archeId: string;
  holderId: string;
  userId: string;
  /**
   * The arche id the plugin local store keys this app's data by, when it is
   * not `archeId`: the tenant the bridge acts as — `test_<archeId>` on the
   * Fias Forge test channel (ADR-023), whose store namespace is the test
   * tenant's while its access is the app's. Host-derived, like the rest.
   */
  storeArcheId?: string;
}

type RoleLossTrigger = 'mount' | 'sheet' | 'refused';

/** What the person could do, as last seen on this device. */
interface SeenAccess {
  mode: string;
  tier: string;
  roles: string[];
  permissions: string[];
  /**
   * The audiences they read (ADR-034 phase 2), sorted. Absent from records
   * written before audiences, and from answers of servers without them.
   */
  audiences?: string[];
}

const TIER_RANK: Record<string, number> = { viewer: 0, member: 1, admin: 2, owner: 3 };

/** The local store namespace the bridge writes — the one held and cleared. */
function storeNamespace(n: RoleLossNamespace): {
  archeId: string;
  holderId: string;
  userId: string;
} {
  return { archeId: n.storeArcheId ?? n.archeId, holderId: n.holderId, userId: n.userId };
}

// Records are per STORE namespace: a clear of one is no clear of another,
// so the live app and its test channel each keep their own.
const storageKey = (n: RoleLossNamespace) => {
  const s = storeNamespace(n);
  return `${KEY_PREFIX}:${s.archeId}:${s.holderId}:${s.userId}`;
};
const reclearKey = (n: RoleLossNamespace) => {
  const s = storeNamespace(n);
  return `${RECLEAR_PREFIX}:${s.archeId}:${s.holderId}:${s.userId}`;
};
const namespaceKey = storageKey;

function readSeen(n: RoleLossNamespace): SeenAccess | null {
  try {
    const raw = localStorage.getItem(storageKey(n));
    const parsed = raw ? (JSON.parse(raw) as Partial<SeenAccess>) : null;
    if (
      parsed &&
      typeof parsed.mode === 'string' &&
      typeof parsed.tier === 'string' &&
      Array.isArray(parsed.roles) &&
      Array.isArray(parsed.permissions)
    ) {
      return {
        mode: parsed.mode,
        tier: parsed.tier,
        roles: parsed.roles,
        permissions: parsed.permissions,
        ...(Array.isArray(parsed.audiences) ? { audiences: parsed.audiences } : {}),
      };
    }
    return null;
  } catch {
    return null;
  }
}

function writeSeen(n: RoleLossNamespace, seen: SeenAccess): void {
  // A check still answering when its user signed out must not write them back.
  if (departed.has(n.userId)) return;
  try {
    localStorage.setItem(storageKey(n), JSON.stringify(seen));
  } catch {
    // per-device convenience only
  }
}

/** The person's access now; `'none'` when it is gone; null when unknown. */
async function currentAccess(n: RoleLossNamespace): Promise<SeenAccess | 'none' | null> {
  try {
    const mine = await api.getMyAppAccess(n.holderId, n.archeId);
    // The approval is gone though roles remain: they reach nothing — access
    // gone, exactly as the route's NOT_FOUND below.
    if (mine.admitted === false) return 'none';
    return {
      // `activating` enforces roles already (ADR-034 phase 2): the same
      // narrowing from tier rules as `roles`.
      mode: mine.mode === 'activating' ? 'roles' : mine.mode,
      tier: mine.tier,
      roles: mine.roles
        .filter((r) => r.eligible)
        .map((r) => r.roleId)
        .sort(),
      permissions: [...mine.permissions].sort(),
      ...(mine.audiences ? { audiences: [...mine.audiences.readable].sort() } : {}),
    };
  } catch (err) {
    // The route's own refusal only (its uniform NOT_FOUND) — never a bare
    // 404, which a route a rolled-back API does not know also answers.
    const e = err as { status?: unknown; code?: unknown };
    return e?.status === 404 && e?.code === 'NOT_FOUND' ? 'none' : null;
  }
}

function narrowed(seen: SeenAccess, now: SeenAccess | 'none'): boolean {
  if (now === 'none') return true;
  if ((TIER_RANK[now.tier] ?? -1) < (TIER_RANK[seen.tier] ?? -1)) return true;
  if (seen.mode !== 'roles' && now.mode === 'roles') return true;
  // An audience they read is gone — only where both sides list audiences.
  const { audiences: before } = seen;
  const { audiences: after } = now;
  if (before && after && before.some((a) => !after.includes(a))) return true;
  return (
    seen.roles.some((r) => !now.roles.includes(r)) ||
    seen.permissions.some((p) => !now.permissions.includes(p))
  );
}

/**
 * What to record for `now`. An answer without audiences (an older server)
 * keeps the audiences this device last saw, so a later answer that lists
 * them is still compared with what the person read.
 */
function toRecord(seen: SeenAccess | null, now: SeenAccess): SeenAccess {
  return now.audiences === undefined && seen?.audiences
    ? { ...now, audiences: seen.audiences }
    : now;
}

function hasReclearMarker(n: RoleLossNamespace): boolean {
  try {
    return localStorage.getItem(reclearKey(n)) !== null;
  } catch {
    return false;
  }
}

function setReclearMarker(n: RoleLossNamespace): void {
  // Same as the record: never written back for a user who signed out.
  if (departed.has(n.userId)) return;
  try {
    localStorage.setItem(reclearKey(n), '1');
  } catch (err) {
    // The clear itself happened; only the second one at the next mount is lost.
    captureMessage('App role loss: the clear-again marker could not be kept', 'warn', {
      source: 'app-role-loss',
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

function dropReclearMarker(n: RoleLossNamespace): void {
  try {
    localStorage.removeItem(reclearKey(n));
  } catch {
    // per-device convenience only; the next mount clears again, harmlessly
  }
}

const inFlight = new Map<string, Promise<boolean>>();
/** Users who left this device since they last arrived — their records stay forgotten. */
const departed = new Set<string>();
const lastRefusalCheck = new Map<string, number>();

async function check(n: RoleLossNamespace, trigger: RoleLossTrigger): Promise<boolean> {
  const started = Date.now();
  return holdPluginLocalStoreNamespace(
    storeNamespace(n),
    async (held) => {
      let cleared = false;
      // A clear made while the app was open last time: clear again, first —
      // before the app's held calls run, and whatever the server says now.
      // A failed clear keeps the marker, so the next mount tries again.
      if (trigger === 'mount' && hasReclearMarker(n)) {
        const { ok } = await held.clear();
        if (ok) {
          dropReclearMarker(n);
          cleared = true;
        }
      }
      const now = await currentAccess(n);
      if (now === null) return cleared;
      const seen = readSeen(n);
      // Only a narrowing FROM access this device saw. The route's refusal
      // also means "this workspace never admitted the app" — and an app runs
      // unapproved in an org workspace, keeping local data there — so a
      // refusal with no record of access clears nothing (ADR-031: the store
      // must never lose a journal it had no reason to drop).
      if (seen && narrowed(seen, now)) {
        const { ok } = await held.clear();
        // A failed clear keeps the old record, so the next check tries again.
        if (!ok) return cleared;
        // The app may hold what it read before this clear and save it back:
        // it is open (any trigger but a mount), or the mount's hold lapsed
        // before the answer came in and its calls ran.
        if (trigger !== 'mount' || Date.now() - started >= MAX_HOLD_MS) setReclearMarker(n);
        if (now === 'none') forget(n);
        else writeSeen(n, toRecord(seen, now));
        return true;
      }
      if (now !== 'none') writeSeen(n, toRecord(seen, now));
      return cleared;
    },
    { maxHoldMs: MAX_HOLD_MS },
  );
}

/**
 * Check for narrowed access and clear the app's local data for this person
 * in this workspace if it narrowed — and, on a mount, first clear what a
 * clear made while the app was last open may have had written back.
 * `trigger` (default `mount`) says when it runs: `mount` as the app opens,
 * `sheet` when its access sheet closes, `refused` after a FORBIDDEN call —
 * rate-limited per namespace. Resolves to whether it cleared.
 */
export async function clearLocalDataOnRoleLoss(
  n: RoleLossNamespace,
  options: { trigger?: RoleLossTrigger } = {},
): Promise<boolean> {
  const key = namespaceKey(n);
  const running = inFlight.get(key);
  if (running) return running;
  if (options.trigger === 'refused') {
    const last = lastRefusalCheck.get(key) ?? 0;
    if (Date.now() - last < REFUSAL_COOLDOWN_MS) return false;
    lastRefusalCheck.set(key, Date.now());
  }
  const run = check(n, options.trigger ?? 'mount').catch(() => false);
  inFlight.set(key, run);
  try {
    return await run;
  } finally {
    inFlight.delete(key);
  }
}

function forget(n: RoleLossNamespace): void {
  try {
    localStorage.removeItem(storageKey(n));
  } catch {
    // per-device convenience only
  }
}

/**
 * Forget the role-loss records — and clear-again markers — of users leaving
 * this device (ADR-031: a departed user's id must not linger in
 * localStorage; their local data is cleared with them, so a marker has
 * nothing left to do) — every user but `exceptUserId`, or only `userId`.
 * Called beside the local store's own identity clears. Never throws.
 */
export function forgetRoleLossRecords(which: { exceptUserId: string } | { userId: string }): void {
  if ('userId' in which) departed.add(which.userId);
  else departed.delete(which.exceptUserId);
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !(key.startsWith(`${KEY_PREFIX}:`) || key.startsWith(`${RECLEAR_PREFIX}:`))) {
        continue;
      }
      const userId = key.slice(key.lastIndexOf(':') + 1);
      const leaving = 'userId' in which ? userId === which.userId : userId !== which.exceptUserId;
      if (leaving) doomed.push(key);
    }
    for (const key of doomed) localStorage.removeItem(key);
  } catch {
    // per-device convenience only
  }
}

/** Test hook: forget in-flight checks and cooldowns. */
export function resetRoleLossChecksForTests(): void {
  inFlight.clear();
  departed.clear();
  lastRefusalCheck.clear();
}
